/**
 * Guard helpers for the claim phase to prevent double-submit races.
 *
 * During the initial claim phase two races can fire "You already own 'X'" from
 * the server (WARCARP-APP-D / -E):
 *
 * 1. The player double-taps the same territory before the server response
 *    arrives and invalidates the React-Query cache.
 * 2. Stale optimistic state briefly shows a territory as valid (e.g. unclaimed)
 *    after the player already claimed it in a previous request that hasn't
 *    propagated yet.
 *
 * `isAlreadyOwnedClaimError` is the classification predicate — pure, no
 * imports — so it can be unit-tested in vitest without any RN or Sentry deps.
 *
 * `buildClaimDeduplicate` returns a stateful helper that short-circuits
 * repeated calls for the same territory within a guard window, preventing
 * the double-submit from ever reaching the network.
 */

/**
 * Returns true when the error is the "You already own 'X'" server rejection
 * that fires during the claim phase when a territory has been claimed by the
 * time the second request arrives (race / double-tap).
 *
 * This is an expected condition — the server is correctly enforcing ownership;
 * the client should not forward it to Sentry as an error.
 */
export function isAlreadyOwnedClaimError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  return /you already own/i.test(msg)
}

/**
 * Returns a deduplication function that ignores repeated calls for the same
 * `territoryId` within `windowMs` milliseconds.
 *
 * Usage:
 *   const dedup = buildClaimDeduplicate()
 *   if (dedup(territoryId)) return  // skip, already in-flight
 *   claim.mutate({ territoryId })
 *
 * The window starts when `dedup` first returns false for a given territory and
 * resets once the window expires, so the player can re-tap after the request
 * resolves (normal case: the cache update removes the territory from the valid
 * set, so the tap is already a no-op; the window is a safety net for the
 * residual race).
 */
export function buildClaimDeduplicate(windowMs = 3_000): (territoryId: string) => boolean {
  const inFlight = new Map<string, number>()
  return (territoryId: string): boolean => {
    const now = Date.now()
    const last = inFlight.get(territoryId)
    if (last !== undefined && now - last < windowMs) {
      return true // duplicate — caller should skip
    }
    inFlight.set(territoryId, now)
    return false // fresh tap — caller should proceed
  }
}
